import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { GetPlayerAuctions, PlayerGetSubscribeManager } from "../../redux/auth-reducer/auth-service";
import { useHubEvent } from "../../hooks/useHubEvent";
import { useHubResync } from "../../hooks/useHubResync";
import { useSearchedPagedList } from "../../hooks/useSearchedPagedList";
import {
  AUCTION_TABS,
  applyPushedLots,
  managerIdsParam,
  managerOptions,
  reminderSearch,
  tabFromQuery,
} from "../../utils/helper/auctionList";

/**
 * The player's Auctions list (Player/AuctionPlayer.js, #236): one row per auction, 10 to a page,
 * on two tabs, searched and filtered by the server (Player/GetAuctions). A new tab, search or
 * Manager filter is a new fetcher, which useSearchedPagedList answers from page 1 (AC6).
 *
 * The tab is in the URL (?tab=closed), so Back from a lot page or Auction Results returns to it; the
 * sidebar and the "Auctions" crumb open Live and Upcoming.
 *
 * Live updates: the lot pushes patch the pool and the open lot of the auctions on the page, and an
 * auction that starts or ends reloads the page quietly, so an upcoming row turns Live without a
 * refresh (AC13). The Worker pushes at an auction's start and every 7 s. A saved auction and a
 * reconnect reload it too.
 *
 * @returns {object} The tab, the page and its rows, the search, the Manager filter, a start
 *   reminder, and the player's actions on the list.
 */
export const useAuctionList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromQuery(searchParams.get("tab"));

  // The Manager filter's options (null until loaded) and the ticked ones: all of them by default.
  const [managers, setManagers] = useState(null);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const managersRef = useRef(null);
  // A start reminder the player arrived from (#196): its auction's row is highlighted.
  const [reminder, setReminder] = useState(null);
  // A reload the player did not ask for keeps the rows on screen instead of the loader.
  const [quiet, setQuiet] = useState(false);

  useEffect(() => {
    PlayerGetSubscribeManager().then((response) => {
      const options = managerOptions(Array.isArray(response?.data) ? response.data : []);
      managersRef.current = options;
      setManagers(options);
      setSelectedManagers(options);
    });
  }, []);

  const managerIds = managerIdsParam(selectedManagers, managers || []);
  const fetcher = useCallback(
    (query) => GetPlayerAuctions({ ...query, Tab: tab, ManagerIds: managerIds }),
    [tab, managerIds]
  );
  const list = useSearchedPagedList(fetcher);

  useEffect(() => {
    if (!list.loading) setQuiet(false);
  }, [list.loading]);

  const reloadQuietly = () => {
    setQuiet(true);
    list.reload();
  };

  // --- Live updates --------------------------------------------------------------------------
  // Decided against the rows as the last render left them; the patch itself is a functional update,
  // so two pushes in one tick both land.
  const rowsRef = useRef(list.rows);
  useLayoutEffect(() => {
    rowsRef.current = list.rows;
  });

  const applyPush = (lots) => {
    if (!Array.isArray(lots)) return;
    if (applyPushedLots(rowsRef.current, lots).needsReload) reloadQuietly();
    list.updateRows((rows) => applyPushedLots(rows, lots).rows);
  };

  useHubEvent("ReceiveBidStatus", applyPush);
  useHubEvent("ReceiveCurrentBid", applyPush);
  // A manager saved an auction, new or edited: the push carries none of this player's flags.
  useHubEvent("GetRecentAuction", reloadQuietly);
  // Messages sent while the connection was down are lost.
  useHubResync(reloadQuietly);

  // --- Start reminders (#196) ----------------------------------------------------------------
  // A start reminder in the bell links here with { auctionId, auctionName } in the router state. The
  // list is searched for the name, on the Live and Upcoming tab, for all managers, and the row is
  // highlighted. Keyed on location.key, so following the same reminder twice counts twice. The state
  // is then cleared, or a reload would apply it again; clearing it also drops ?tab.
  useEffect(() => {
    const auctionId = Number(location.state?.auctionId);
    if (!Number.isInteger(auctionId) || auctionId <= 0) return;

    setReminder({ auctionId, arrival: location.key });
    setSelectedManagers(managersRef.current || []);
    list.setSearchText(reminderSearch(location.state?.auctionName));
    navigate(location.pathname, { replace: true, state: null });
    // Only a new arrival may apply it: the clearing navigate above is itself a location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // --- The player's actions ------------------------------------------------------------------
  const selectTab = (next) =>
    setSearchParams(next === AUCTION_TABS.CLOSED ? { tab: "closed" } : {}, { replace: true });

  return {
    tab,
    selectTab,
    rows: list.rows,
    page: list.page,
    totalPages: list.totalPages,
    totalRecords: list.totalRecords,
    loading: list.loading && !quiet,
    failed: list.failed,
    nextPage: list.nextPage,
    previousPage: list.previousPage,
    searchText: list.searchText,
    setSearchText: list.setSearchText,
    clearSearch: list.clearSearch,
    managers,
    selectedManagers,
    selectManagers: setSelectedManagers,
    // Whether the search or the Manager filter narrows the list, for the empty table's hint.
    narrowed: list.searchText.trim() !== "" || managerIds !== "",
    reminder,
    reload: reloadQuietly,
  };
};
